import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// types
import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import { config, gridSpacing } from 'store/baseConfig';

// assets
import { IconTallymark1 } from '@tabler/icons-react';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import { NavLink } from 'react-router-dom';
import getRouteNameByPath from 'utils/uitlsRouteNameByPath';

const linkSX = {
    display: 'flex',
    color: 'grey.900',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center',
    '&:hover': {
        color: 'primary.main'
    },
};

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({ card, divider, icon, icons, maxItems, navigation, rightAlign, separator, title, titleBottom, ...others }) => {

    const theme = useTheme();

    const iconStyle = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '1rem',
        height: '1rem',
        color: theme.palette.secondary.main
    };

    const location = useLocation();

    const [main, setMain] = useState();
    const [item, setItem] = useState();
    const [subItem, setSubItem] = useState();
    const [group, setGroup] = useState();

    // set active item state
    const getCollapse = (menu) => {
        if (menu.children) {
            menu.children.filter((collapse) => {
                if (collapse.type && collapse.type === 'collapse') {
                    getCollapse(collapse);
                } else if (collapse.type && collapse.type === 'item') {
                    // URL에서 쿼리 파라미터 제거
                    const currentPath = document.location.pathname;
                    const menuPath = collapse.url.split('?')[0];
                    
                    if (currentPath === config.basename + menuPath) {
                        // form 페이지인 경우 type 파라미터 확인
                        if (currentPath === config.basename + '/e-system/form') {
                            const searchParams = new URLSearchParams(document.location.search);
                            const type = searchParams.get('type');
                            // 현재 메뉴의 formType과 URL의 type이 일치하는 경우
                            if (type === collapse.formType) {
                                setMain(menu);
                                setItem(collapse);
                                setSubItem(null);
                            }
                        } else {
                            setMain(menu);
                            setItem(collapse);
                            setSubItem(null);
                        }
                    } else if (currentPath.startsWith(config.basename + menuPath)) {
                        setMain(menu);
                        setItem(collapse);
                        setSubItem(getRouteNameByPath(currentPath));
                    }
                }
                return false;
            });
        }
    };

    useEffect(() => {
        navigation?.items?.map((menu) => {
            if (menu.type && menu.type === 'group' && document.location.pathname.startsWith(config.basename + menu.url) ) {
                setGroup(menu);
                getCollapse(menu);
            }
            return false;
        });
    }, [navigation, location]); // location을 의존성 배열에 추가

    // item separator
    const SeparatorIcon = separator;
    const separatorIcon = separator ? <SeparatorIcon stroke={1.5} size="1rem" /> : <IconTallymark1 stroke={1.5} size="1rem" />;

    let groupContent;
    let mainContent;
    let itemContent;
    let breadcrumbContent = <Typography />;
    let itemTitle = '';
    let GroupIcon;
    let CollapseIcon;
    let ItemIcon;
    let subItemContent;

    // group item
    if (group && group.type === 'group'){
        GroupIcon = group.icon ? group.icon : AccountTreeTwoToneIcon;
        groupContent = group.title ? (
            <Typography variant="subtitle1">
                {icons && <GroupIcon style={iconStyle} />}
                {group.title}
            </Typography>
        ) : null;
    }

    // collapse item
    if (main && main.type === 'collapse') {
        CollapseIcon = main.icon ? main.icon : AccountTreeTwoToneIcon;
        mainContent = (
            <Typography variant="subtitle1">
                {icons && <CollapseIcon style={iconStyle} />}
                {main.title}
            </Typography>
        );
    }

    if (subItem) {
        subItemContent = (
            <Typography
                variant="subtitle1"
                sx={{
                    display: 'flex',
                    textDecoration: 'none',
                    alignContent: 'center',
                    alignItems: 'center',
                    color: 'grey.500'
                }}
            >
                {subItem}
            </Typography>
        );
    }

    // items
    if (item && item.type === 'item') {
        itemTitle = item.title;

        ItemIcon = item.icon ? item.icon : AccountTreeTwoToneIcon;
        if(subItem){
            itemContent = (
                <Typography
                    component={NavLink} to={item.url} variant="subtitle1" sx={linkSX} >
                    {icons && <ItemIcon style={iconStyle} />}
                    {itemTitle}
                </Typography>
            );
        }else{
            itemContent = (
                <Typography
                    variant="subtitle1"
                    sx={{
                        display: 'flex',
                        textDecoration: 'none',
                        alignContent: 'center',
                        alignItems: 'center',
                        color: 'grey.500'
                    }}
                >
                    {icons && <ItemIcon style={iconStyle} />}
                    {itemTitle}
                </Typography>
            );
        }
        

        // main
        if (item.breadcrumbs !== false) {
            breadcrumbContent = (
                <Card
                    sx={{
                        marginBottom: card === false ? 0 : theme.spacing(gridSpacing),
                        border: card === false ? 'none' : '1px solid',
                        borderColor: theme.palette.primary[200] + 75,
                        background: card === false ? 'transparent' : theme.palette.background.default
                    }}
                    {...others}
                >
                    <Box sx={{ p: 2, pl: card === false ? 0 : 2 }}>
                        <Grid
                            container
                            direction={rightAlign ? 'row' : 'column'}
                            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                            alignItems={rightAlign ? 'center' : 'flex-start'}
                            spacing={1}
                        >
                            {title && !titleBottom && (
                                <Grid item>
                                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                        {item.title} {subItem && `${subItem}`}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item>
                                <MuiBreadcrumbs
                                    sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
                                    aria-label="breadcrumb"
                                    maxItems={maxItems || 8}
                                    separator={separatorIcon}
                                >
                                    <Typography component={NavLink} href="/" color="inherit" variant="subtitle1" sx={linkSX}>
                                        {icons && <HomeTwoToneIcon sx={iconStyle} />}
                                        {icon && <HomeIcon sx={{ ...iconStyle, mr: 0 }} />}
                                        {!icon && '홈'}
                                    </Typography>
                                    {groupContent}
                                    {mainContent}
                                    {itemContent}
                                    {subItemContent}
                                </MuiBreadcrumbs>
                            </Grid>
                            {title && titleBottom && (
                                <Grid item>
                                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                        {item.title}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    {card === false && divider !== false && <Divider sx={{ borderColor: theme.palette.primary.main, mb: gridSpacing }} />}
                </Card>
            );
        }
    }

    return breadcrumbContent;
};

Breadcrumbs.propTypes = {
    card: PropTypes.bool,
    divider: PropTypes.bool,
    icon: PropTypes.bool,
    icons: PropTypes.bool,
    maxItems: PropTypes.number,
    navigation: PropTypes.object,
    rightAlign: PropTypes.bool,
    separator: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    title: PropTypes.bool,
    titleBottom: PropTypes.bool
};

export default Breadcrumbs;